import { useContext, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { SeoCaptureContext } from "@/lib/pageData";
import { BUSINESS, SITE_URL, compactDescription, businessSchema } from "@/lib/business";

export function SEO({ title, description, canonicalPath, jsonLd, noindex, pageType = "WebPage" }) {
  const location = useLocation();
  const capture = useContext(SeoCaptureContext);
  const canonical = SITE_URL + (canonicalPath || location.pathname);
  const summary = compactDescription(description);
  const supplied = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];
  const organizationId = SITE_URL + "/#organization";
  const websiteId = SITE_URL + "/#website";
  const pageId = canonical + "#webpage";
  const entities = supplied.map((item) => item["@type"] === "BreadcrumbList"
    ? { ...item, "@id": item["@id"] || canonical + "#breadcrumb" } : item);
  const mainEntity = entities.find((item) => item["@type"] === "Service" || item["@type"] === "ItemList");
  const breadcrumb = entities.find((item) => item["@type"] === "BreadcrumbList");
  const structured = [
    ...(entities.some((item) => item["@id"] === organizationId) ? [] : [businessSchema()]),
    {
      "@context": "https://schema.org", "@type": "WebSite", "@id": websiteId,
      url: SITE_URL + "/", name: "Mynavodayans", alternateName: BUSINESS.name,
      publisher: { "@id": organizationId }, inLanguage: "en-IN",
    },
    {
      "@context": "https://schema.org", "@type": pageType, "@id": pageId,
      url: canonical, name: title, description: summary, inLanguage: "en-IN",
      isPartOf: { "@id": websiteId }, publisher: { "@id": organizationId },
      ...(mainEntity?.["@id"] ? { mainEntity: { "@id": mainEntity["@id"] } } : {}),
      ...(breadcrumb ? { breadcrumb: { "@id": breadcrumb["@id"] } } : {}),
    },
    ...entities,
  ];
  const structuredString = JSON.stringify(structured);
  if (capture) capture.current = { title, description: summary, canonical, jsonLd: structured, noindex: Boolean(noindex) };

  useEffect(() => {
    document.title = title;
    const setMeta = (name, content, attr = "name") => {
      let element = document.head.querySelector(`meta[${attr}="${name}"]`);
      if (!element) {
        element = document.createElement("meta");
        element.setAttribute(attr, name);
        document.head.appendChild(element);
      }
      element.setAttribute("content", content);
    };
    if (summary) setMeta("description", summary);
    setMeta("robots", noindex ? "noindex, follow" : "index, follow");
    setMeta("og:title", title, "property");
    setMeta("og:description", summary, "property");
    setMeta("og:type", "website", "property");
    setMeta("og:url", canonical, "property");
    setMeta("og:site_name", "Mynavodayans", "property");
    setMeta("og:image", BUSINESS.logo, "property");
    setMeta("og:image:alt", BUSINESS.name + " logo", "property");
    setMeta("twitter:card", "summary");
    setMeta("twitter:title", title);
    setMeta("twitter:description", summary);
    setMeta("twitter:image", BUSINESS.logo);

    let link = document.head.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.setAttribute("href", canonical);
    document.head.querySelectorAll("script[data-page-jsonld]").forEach((s) => s.remove());
    let script;
    if (structuredString) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.dataset.pageJsonld = "1";
      script.textContent = structuredString;
      document.head.appendChild(script);
    }
    return () => { if (script) script.remove(); };
  }, [title, summary, canonical, noindex, structuredString]);
  return null;
}
